import {
  AlarmClock, Ban, ChevronDown, ChevronUp, CircleCheck, ClipboardList, Clock, Flag, Hourglass, type LucideIcon,
  Pause, PhoneCall,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader } from "@/components/ui/card";
import {
  type CadenceStatus, type Tone, atRest, cadenceStatus, cycleWords, decisionText, phaseDay, priorityReasonText, requestText,
} from "@/lib/cadence";
import { leadStateText } from "@/lib/campaign";
import { dialText } from "@/lib/contacts";
import type { LeadCadence, LeadCadenceMembership, LeadCard } from "@/lib/types";
import { cn, formatDateTime, relativeTime } from "@/lib/utils";

/**
 * The Follow-up card beside a contact record: the lead's journey on each cadence campaign, read
 * the way the dialer reads it (ADR-066). One block per campaign: what the dialer will do next
 * and why, where the lead stands in the cadence, and the decisions that put it there.
 *
 * The words are `lib/cadence.ts`'s, so the campaign page's buckets and this card never disagree
 * about what "overdue" or "held" means. Nothing here writes: the pause, resume and re-enroll
 * actions live in the record's header menu.
 */

const HERO: Record<Tone, { panel: string; icon: string }> = {
  accent: { panel: "border-accent-4 bg-accent-2", icon: "text-accent-11 ring-accent-4" },
  success: { panel: "border-success-4 bg-success-2", icon: "text-success-11 ring-success-4" },
  warning: { panel: "border-warning-4 bg-warning-2", icon: "text-warning-11 ring-warning-4" },
  danger: { panel: "border-danger-4 bg-danger-2", icon: "text-danger-11 ring-danger-4" },
  neutral: { panel: "border-border bg-gray-2 dark:bg-gray-1", icon: "text-gray-11 ring-gray-5" },
  outline: { panel: "border-border bg-surface", icon: "text-text-muted ring-border" },
};

const DOT: Record<Tone, string> = {
  accent: "bg-accent-9",
  success: "bg-success-9",
  warning: "bg-warning-9",
  danger: "bg-danger-9",
  neutral: "bg-gray-7",
  outline: "bg-gray-5",
};

/** The newest decision's dot sits in a soft ring of its own tone, so the eye lands on it first. */
const HALO: Record<Tone, string> = {
  accent: "ring-accent-3",
  success: "ring-success-2",
  warning: "ring-warning-2",
  danger: "ring-danger-2",
  neutral: "ring-gray-4",
  outline: "ring-gray-3",
};

const ICON: Record<CadenceStatus["kind"], LucideIcon> = {
  due: Clock,
  overdue: AlarmClock,
  held: Pause,
  on_call: PhoneCall,
  needs_outcome: ClipboardList,
  rest: Flag,
};

/** How many of the dialer's decisions show before "Show more". */
const DECISIONS_FOLDED = 3;

/** "Oct 24", with the year only once it is not this one. */
function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const thisYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(thisYear ? {} : { year: "numeric" }) });
}

function heroIcon(status: CadenceStatus, state: string): LucideIcon {
  if (status.kind !== "rest") return ICON[status.kind];
  if (state === "done") return CircleCheck;
  if (state === "dnc" || state === "blocked") return Ban;
  return Flag;
}

/** The pill by the campaign's name: the lead-level facts core-api adds (held, do not call) win
 *  over the dialer's state; otherwise the state itself. */
function pill(m: LeadCadenceMembership) {
  if (m.dial === "held" || m.dial === "do_not_call") return dialText(m.dial);
  return leadStateText(m.state);
}

function Tile({
  label, value, sub, note, meter, title,
}: {
  label: string;
  value: React.ReactNode;
  /** Beside the value, in a smaller face: "of 40", "call 2 next". */
  sub?: string | null;
  /** Under the value, allowed to wrap: the priority's reason. */
  note?: string | null;
  /** 0..1 fills a thin bar under the value. */
  meter?: number;
  title?: string;
}) {
  return (
    <div className="min-w-0 rounded-md bg-gray-2 px-2.5 py-2 dark:bg-gray-1" title={title}>
      <dt className="text-2xs font-medium uppercase tracking-wide text-text-subtle">{label}</dt>
      <dd className="mt-0.5 min-w-0">
        <span className="text-sm font-semibold tabular-nums text-text">{value}</span>
        {sub && <span className="ml-1 text-xs text-text-muted">{sub}</span>}
        {note && <span className="block text-2xs leading-4 text-text-muted">{note}</span>}
        {meter !== undefined && (
          <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-gray-4" aria-hidden>
            <span
              className="block h-full rounded-full bg-accent-9 transition-[width] duration-300"
              style={{ width: `${Math.round(Math.min(1, Math.max(0, meter)) * 100)}%` }}
            />
          </span>
        )}
      </dd>
    </div>
  );
}

function Membership({ m, contact, now }: { m: LeadCadenceMembership; contact: LeadCard; now: number }) {
  const [unfolded, setUnfolded] = React.useState(false);
  const status = cadenceStatus(m, now);
  const Icon = heroIcon(status, m.state);
  const badge = pill(m);
  const max = contact.campaigns.find((c) => c.campaign_id === m.campaign_id)?.max_attempts ?? null;
  const day = phaseDay(m.priority_reason);
  // A row at rest ranks nowhere: its priority is 0 and its reason is the state, not a band.
  const priorityWhy = atRest(m.state) ? null : priorityReasonText(m.priority_reason);
  const decisions = unfolded ? m.decisions : m.decisions.slice(0, DECISIONS_FOLDED);
  const folded = m.decisions.length - DECISIONS_FOLDED;
  const [slug, version] = (m.cadence ?? "").split("@");

  return (
    <section className="space-y-3 px-4 py-3" aria-label={`Follow-up on ${m.campaign_name ?? "a campaign"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={`/campaigns/${m.campaign_id}?tab=cadence`}
            className="block text-sm font-medium leading-5 text-text hover:underline"
          >
            {m.campaign_name ?? "Deleted campaign"}
          </Link>
          <p className="text-2xs leading-4 text-text-subtle">
            {slug && <span className="font-mono">{slug}{version ? ` v${version}` : ""}</span>}
            {slug && m.expires_at && " · "}
            {m.expires_at && <span title={formatDateTime(m.expires_at)}>on the list until {shortDay(m.expires_at)}</span>}
          </p>
        </div>
        <Badge tone={badge.tone} size="sm" title={badge.description} className="shrink-0">{badge.label}</Badge>
      </div>

      <div className={cn("flex items-start gap-3 rounded-lg border p-3", HERO[status.tone].panel)}>
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full bg-surface shadow-xs ring-1",
            HERO[status.tone].icon,
          )}
          aria-hidden
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold text-text">
            <span title={status.at ? formatDateTime(status.at) : undefined}>{status.headline}</span>
            {status.kind === "overdue" && (
              <Badge tone="warning" size="sm" title="Due for more than fifteen minutes with nobody taking it">Overdue</Badge>
            )}
          </p>
          {status.detail && <p className="text-xs leading-4 text-text-muted">{status.detail}</p>}
        </div>
      </div>

      {m.pending_requests.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning-4 bg-warning-2 px-3 py-2 text-xs text-warning-11">
          <Hourglass className="mt-px size-3.5 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="font-medium">Waiting for the dialer</p>
            <ul className="space-y-0.5">
              {m.pending_requests.map((r) => (
                <li key={r.id}>
                  {requestText(r.kind)} · asked {relativeTime(r.requested_at)}
                  {r.reason && <span className="opacity-80"> · “{r.reason}”</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-2">
        <Tile
          label="Phase"
          value={m.phase ? m.phase[0]!.toUpperCase() + m.phase.slice(1).replaceAll("_", " ") : "Past the end"}
          sub={day}
        />
        <Tile label="Cycle" value={m.cycle} sub={cycleWords(m)} />
        <Tile
          label="Attempts"
          value={m.attempts}
          sub={max ? `of ${max}` : null}
          meter={max ? m.attempts / max : undefined}
          title={m.technical_retries ? `${m.technical_retries} technical retr${m.technical_retries === 1 ? "y" : "ies"} not counted` : undefined}
        />
        <Tile label="Priority" value={m.priority} note={priorityWhy} />
      </dl>

      {m.decisions.length > 0 && (
        <div>
          <p className="mb-2 text-2xs font-medium uppercase tracking-wide text-text-subtle">What the dialer decided</p>
          <ol className="relative ml-1 space-y-3 border-l border-border pl-4">
            {decisions.map((d, i) => {
              const words = decisionText(d);
              return (
                <li key={d.id} className="relative">
                  <span
                    className={cn(
                      "absolute -left-[21px] top-1 size-2.5 rounded-full",
                      DOT[words.tone],
                      i === 0 ? cn("ring-[3px]", HALO[words.tone]) : "ring-2 ring-surface",
                    )}
                    aria-hidden
                  />
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className={cn("text-xs text-text", i === 0 ? "font-semibold" : "font-medium")}>
                        {words.title}
                        {d.shadow && <Badge tone="outline" size="sm" className="ml-1.5 align-middle">shadow</Badge>}
                      </p>
                      {words.detail && <p className="text-2xs leading-4 text-text-muted">{words.detail}</p>}
                    </div>
                    <time
                      dateTime={d.at}
                      title={formatDateTime(d.at)}
                      className="shrink-0 whitespace-nowrap text-2xs text-text-subtle"
                    >
                      {relativeTime(d.at)}
                    </time>
                  </div>
                </li>
              );
            })}
          </ol>
          {folded > 0 && (
            <button
              type="button"
              onClick={() => setUnfolded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
            >
              {unfolded ? <ChevronUp className="size-3.5" aria-hidden /> : <ChevronDown className="size-3.5" aria-hidden />}
              {unfolded ? "Show fewer" : `Show ${folded} more`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** The lead's journey on each cadence campaign. Nothing when it is on none. */
export function FollowUpCard({
  contact, cadence, now,
}: {
  contact: LeadCard;
  cadence: LeadCadence | undefined;
  now: number;
}) {
  const rows = (cadence?.memberships ?? []).filter((m) => m.cadence);
  if (rows.length === 0) return null;
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Follow-up"
        description="Where the dialer has this lead, and why."
        action={
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-2xs text-text-muted"
            title="Read the way the dialer reads it, refreshed every half minute"
          >
            <span className="size-1.5 rounded-full bg-success-9" aria-hidden />
            Live
          </span>
        }
      />
      <div className="divide-y divide-border">
        {rows.map((m) => (
          <Membership key={m.campaign_lead_id} m={m} contact={contact} now={now} />
        ))}
      </div>
    </Card>
  );
}
